<template>
  <div id="appointment-base">
    <div
      class="sticky top-18 z-10 px-6 h-12 flex-shrink-0 flex items-center justify-between gap-y-2 gap-x-4 bg-pure-white border-b border-slate-400"
    >
      <BlBreadcrumbs :breadcrumbs="breadcrumbs" />
    </div>

    <div v-if="pageLoading" class="h-128 w-full grid place-items-center">
      <div class="blum-loader"></div>
    </div>
    <div v-else class="w-full p-6 flex justify-center">
      <div class="bg-pure-white w-full" :style="{ maxWidth: '62.5rem' }">
        <div class="border border-slate-400 py-4 px-6 flex flex-col gap-1">
          <div class="flex">
            <BlChip v-if="isOrphan" chip="Orphan Appointment" color="tomato" />
            <BlChip v-else :chip="`${appointment.status}`" />
          </div>
          <BlTextTitle4
            v-if="appointment.client && appointment.client.name"
            :text="appointment.client.name"
          />
          <BlTextBody2 :text="`Session scheduled for ${timeslot}`" />
        </div>
        <div class="flex">
          <div class="w-160 flex-shrink-0 border border-slate-400 p-6">
            <div class="grid grid-cols-3 gap-4">
              <div class="col-span-3 flex items-center gap-4">
                <BlTextSubtitle1 text="Appointment details" />
              </div>
              <!--  -->
              <BlTextBody2 class="col-span-1" text="Appointment ID" />
              <BlTextBody2
                class="col-span-2 text-slate-1000"
                :text="appointment.id"
              />
              <!--  -->
              <BlTextBody2 class="col-span-1" text="Assigned therapist" />
              <div v-if="isOrphan" class="col-span-2">
                <BlIcon icon="warning" color="tomato-7" class="h-4 w-4" />
              </div>
              <BlTextBody2
                v-else-if="!appointment.user"
                text="-"
                class="col-span-2 text-slate-1000"
              />
              <BlTextBody2
                v-else
                :title="appointment.user.name || 'Deleted user'"
                :text="appointment.user.name || 'Deleted user'"
                class="col-span-2 text-slate-1000"
                :class="{ 'italic ': !appointment.user.name }"
              />
              <!--  -->
              <BlTextBody2 class="col-span-1" text="Supervisor" />
              <BlTextBody2
                v-if="
                  !appointment.supervisors || !appointment.supervisors.length
                "
                class="col-span-2 text-slate-1000"
                text="-"
              />
              <BlTextBody2
                v-else
                class="col-span-2 text-slate-1000"
                :text="appointment.supervisors.map(i => i.name).join(', ')"
              />
              <!--  -->
              <BlTextBody2 class="col-span-1" text="Timeslot" />
              <BlTextBody2
                class="col-span-2 text-slate-1000"
                :text="timeslot"
              />
              <!--  -->
              <BlTextBody2
                v-if="enableBranch"
                class="col-span-1"
                text="Branch"
              />
              <div v-if="enableBranch" class="col-span-2 flex">
                <BlTextBody2 v-if="!appointment.room.branch" :text="'-'" />
                <div
                  v-else
                  class="h-7 max-w-96 px-2 flex items-center justify-center rounded"
                  :style="{
                    backgroundColor:
                      appointment.room.branch.background_color || 'unset'
                  }"
                >
                  <BlTextBody2
                    class="truncate"
                    :text="appointment.room.branch.name || '-'"
                    :style="{
                      color: appointment.room.branch.color || 'unset'
                    }"
                  />
                </div>
              </div>
              <BlTextBody2 class="col-span-1" text="Room" />
              <div class="col-span-2">
                <BlTextBody2
                  class="text-slate-1000"
                  :text="appointment.room.name"
                />
                <BlTextBody2
                  v-if="appointment.is_open_room"
                  class="text-slate-1000"
                  text="(open room)"
                />
              </div>
            </div>
            <!--  -->
            <div class="flex-grow h-px bg-slate-400 my-6"></div>
            <!--  -->
            <div class="grid grid-cols-3 gap-4">
              <div class="col-span-3 flex items-center gap-4">
                <BlTextSubtitle1 text="Credits details" />
                <router-link :to="`/clients/${appointment.client.id}/credit`">
                  <BlInputButton type="outlined" dense label="View credits" />
                </router-link>
              </div>
              <!--  -->
              <BlTextBody2 class="col-span-1" text="Total credits used" />
              <BlTextBody2
                v-if="!appointment.credit_transaction_services.length"
                class="col-span-2 text-slate-1000"
                :text="`${creditsUsed} credit(s)`"
              />
              <BlTextBody2
                v-else
                class="col-span-2 text-slate-1000"
                :text="
                  `${creditsUsed} credit(s) of ${appointment.credit_transaction_services[0].package_item_name}`
                "
              />
              <!--  -->
              <BlTextBody2 class="col-span-1" text="Transaction ID" />
              <BlTextBody2
                v-if="!appointment.credit_transaction_services.length"
                class="col-span-2 text-slate-1000"
                :text="'-'"
              />
              <div v-else class="col-span-2">
                <div
                  v-for="credit in appointment.credit_transaction_services"
                  :key="credit.id"
                  class="px-2 flex items-center gap-2"
                >
                  <div class="h-1 w-1 flex-shrink-0 bg-slate-800 rounded"></div>
                  <div>
                    <BlTextBody2
                      :text="
                        `ID ${credit.credit_transaction_id}:`
                      "
                    />
                    <BlTextBody2 :text="`${credit.session_credit || 0} credit(s)`" />
                  </div>
                </div>
              </div>
            </div>
            <!--  -->
            <div
              v-if="appointment.status === 'cancelled'"
              class="flex-grow h-px bg-slate-400 my-6"
            ></div>
            <!--  -->
            <div
              v-if="appointment.status === 'cancelled'"
              class="grid grid-cols-3 gap-4"
            >
              <div class="col-span-3 flex items-center gap-4">
                <BlTextSubtitle1 text="Cancellation details" />
              </div>
              <!--  -->
              <BlTextBody2
                class="col-span-1"
                text="Cancellation requested by"
              />
              <BlTextBody2
                class="col-span-2 text-slate-1000"
                :text="appointment.cancellation_requester_type"
              />
              <!--  -->
              <BlTextBody2
                class="col-span-1 truncate"
                text="Credits deducted for cancellation"
                title="Credits deducted for cancellation"
              />
              <BlTextBody2
                class="col-span-2 text-slate-1000"
                :text="
                  appointment.subtract_session_credit
                    ? 'Yes'
                    : appointment.subtract_session_credit === null
                    ? '-'
                    : 'No'
                "
              />
              <!--  -->
              <BlTextBody2 class="col-span-1" text="Cancellation reason" />
              <BlTextBody2
                class="col-span-2 text-slate-1000 capitalize-first"
                :text="cancellationReasonText"
              />
            </div>
            <!--  -->
            <div class="flex-grow h-px bg-slate-400 my-6"></div>
            <!--  -->
            <div v-if="appointment.session" class="grid grid-cols-3 gap-4">
              <div class="col-span-3 flex items-center gap-4">
                <BlTextSubtitle1 text="Session details" />
                <router-link
                  v-if="appointment.session.slug"
                  :to="
                    `/session/${appointment.session.slug}${
                      appointment.status === 'scheduled' ||
                      appointment.status === 'booking_in_progress'
                        ? '/edit'
                        : ''
                    }`
                  "
                >
                  <BlInputButton type="outlined" dense label="View session" />
                </router-link>
              </div>
              <!--  -->
              <BlTextBody2 class="col-span-1" text="Session ID" />
              <BlTextBody2
                class="col-span-2 text-slate-1000"
                :text="appointment.session.id"
              />
              <!--  -->
              <BlTextBody2 class="col-span-1" text="Session name" />
              <BlTextBody2
                class="col-span-2 text-slate-1000"
                :text="appointment.session.name || 'Unnamed session'"
              />
              <!--  -->
              <BlTextBody2 class="col-span-1" text="Conducted by" />
              <BlTextBody2
                v-if="!appointment.session.user"
                class="col-span-2 text-slate-1000"
                :text="'-'"
              />
              <BlTextBody2
                v-else
                class="col-span-2 text-slate-1000"
                :text="appointment.session.user.name || '-'"
              />
              <!--  -->
              <BlTextBody2 class="col-span-1" text="Session conducted at" />
              <BlTextBody2
                class="col-span-2 text-slate-1000"
                :text="sessionConductedAt"
              />
              <!--  -->
              <BlTextBody2 class="col-span-1" text="Session duration" />
              <BlTextBody2
                v-if="!appointment.session.duration[0]"
                class="col-span-2 text-slate-1000"
                :text="'-'"
              />
              <BlTextBody2
                v-else
                class="col-span-2 text-slate-1000"
                :text="appointment.session.duration[1] || '-'"
              />
            </div>
          </div>
          <div class="w-90 flex-shrink-0 border border-slate-400 p-6">
            <div class="mb-4">
              <BlTextSubtitle1 text="Activity" />
            </div>
            <BlActivity
              v-for="(activity, idx) in appointment.log_activities"
              :key="activity.id"
              :activity="activity"
              :isFirst="idx === 0"
              :isLast="idx + 1 === appointment.log_activities.length"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import moment from "moment";

import BlBreadcrumbs from "../BlBreadcrumbs.vue";
import BlTextTitle4 from "../BlTextTitle4.vue";
import BlTextSubtitle1 from "../BlTextSubtitle1.vue";
import BlTextBody2 from "../BlTextBody2.vue";
import BlIcon from "../BlIcon.vue";
import BlChip from "../BlChip.vue";
import BlInputButton from "../BlInputButton.vue";
import BlActivity from "../BlActivity.vue";

export default {
  name: "Appointment",
  components: {
    BlBreadcrumbs,
    BlTextTitle4,
    BlTextSubtitle1,
    BlTextBody2,
    BlIcon,
    BlChip,
    BlInputButton,
    BlActivity
  },
  data() {
    return {
      pageLoading: true
    };
  },
  computed: {
    ...mapGetters([
      "currentUser",
      "currentCenter",
      "centerReasonSettings",
      "appointment"
    ]),
    enableBranch() {
      return this.currentCenter.enable_branch;
    },
    breadcrumbs() {
      const appointmentId = Number(this.$route.params.id);
      const appointmentLabel =
        (this.appointment && this.appointment.id) !== appointmentId
          ? `${appointmentId} - Appointment`
          : `${appointmentId} - ${this.appointment.client.name}`;
      return [
        { label: "Appointments", target: `/appointments?preview_type=list` },
        {
          label: appointmentLabel,
          target: `/appointments/details/${appointmentId}`,
          truncate: true
        }
      ];
    },
    isOrphan() {
      return !this.appointment.user && this.appointment.status === "scheduled";
    },
    creditsUsed() {
      if (!this.appointment) return 0;
      return this.getSlotCount(
        this.appointment.start_time_string,
        this.appointment.end_time_string
      );
    },
    timeslot() {
      const date = moment(this.appointment.date).format("dddd, DD MMMM YYYY");
      const time = `(${this.appointment.start_time_string} - ${this.appointment.end_time_string})`;
      return `${date} ${time}`;
    },
    cancellationReasonText() {
      if (!this.appointment) return "-";
      if (!this.appointment.cancellation_reason) return "-";

      const cancelReason = this.appointment.cancellation_reason;
      const cancelCenterReason = this.centerReasonSettings.find(
        i => i.formatted_name === cancelReason
      );

      if (cancelCenterReason) {
        if (cancelCenterReason.is_default) {
          return (
            this.appointment.other_cancellation_reason ||
            cancelCenterReason.name
          );
        } else return cancelCenterReason.name;
      }
      if (cancelReason) return cancelReason.replaceAll("_", " ");
    },
    sessionConductedAt() {
      const session = this.appointment.session;
      if (!session) return "-";
      if (!session.start_time) return "-";

      const start = moment(session.start_time)
        .format("dddd, DD MMMM YYYY|HH:mm")
        .split("|");
      const end = moment(session.end_time)
        .format("dddd, DD MMMM YYYY|HH:mm")
        .split("|");

      if (start[0] === end[0]) {
        if (start[1] === end[1]) {
          return `${start[0]} ${start[1]}`;
        }
        return `${start[0]} (${start[1]} - ${end[1]})`;
      } else {
        return `${start[0]} ${start[1]} - ${end[0]} ${end[1]}`;
      }
    }
  },
  created() {
    this.getCenterReasonSettings();
  },
  async mounted() {
    if (!this.currentCenter.enable_appointment) {
      return this.$router.push({ name: "Clients" });
    }

    const id = this.$route.params.id;

    this.pageLoading = true;
    await this.getAppointment({ id });
    this.pageLoading = false;
  },
  destroyed() {
    const app = document.getElementById("app");
    if (app) app.scroll({ top: 0, left: 0 });
  },
  methods: {
    ...mapActions(["getCenterReasonSettings", "getAppointment"]),
    getSlotCount(start, end) {
      const s = start.split(":").map(i => Number(i));
      const e = end.split(":").map(i => Number(i));
      let slots = 0;
      slots += (e[0] - s[0]) * 2;
      if (s[1] === 30) slots -= 1;
      if (e[1] === 30) slots += 1;
      return slots;
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/appointments/Appointment.vue